import type { Rij } from "./ontwerpTypes";

/**
 * De gegevens waarmee het voorbeeld standaard tekent.
 *
 * <p>Dit is geen vulling maar een testopstelling. Voorbeelddata waarin alles netjes is -- korte
 * namen, alles ingevuld, alles op tijd -- laat elk ontwerp slagen en verbergt precies de problemen
 * die je wilt zien voordat een tegel op het dashboard van een asiel staat. Daarom zit hier
 * opzettelijk in:
 *
 * <ul>
 *   <li>een naam die veel te lang is voor een smalle tegel;</li>
 *   <li>lege velden, want niet alles is altijd ingevuld;</li>
 *   <li>tijdstippen die al verstreken zijn en tijdstippen die nog komen;</li>
 *   <li>afgevinkte en niet-afgevinkte momenten door elkaar;</li>
 *   <li>een dier zonder foto, en bij voeding een gast zonder plan.</li>
 * </ul>
 *
 * <p>Zo zie je in het voorbeeld meteen of je waarschuwing werkt, of je indeling overleeft wat een
 * echte dierennaam is, en of een lege dosering een gat achterlaat.
 */

/** Een tijd van vandaag, een aantal uren voor of na nu; zo is "verstreken" altijd waar het hoort. */
function uurVerschoven(uren: number): string {
  const moment = new Date();
  moment.setHours(moment.getHours() + uren);
  return `${String(moment.getHours()).padStart(2, "0")}:${String(moment.getMinutes()).padStart(2, "0")}`;
}

const MEDICATIE: Rij[] = [
  {
    "dier.naam": "Bo",
    "dier.foto": "",
    "dier.soort": "Hond",
    "dier.verblijf": "Hok 3",
    "klant.naam": "Jansen",
    "medicatie.naam": "Metacam",
    "medicatie.dosering": "0,5 ml",
    "medicatie.tijdstip": uurVerschoven(-3),
    "medicatie.afgevinkt": false,
    "medicatie.door": "",
    "medicatie.toelichting": "Na het eten geven",
  },
  {
    "dier.naam": "Babao van de Hoge Dijk",
    "dier.foto": "",
    "dier.soort": "Hond",
    "dier.verblijf": "Quarantaine 1",
    "klant.naam": "Van den Berg-Hofstede",
    "medicatie.naam": "Synulox",
    "medicatie.dosering": "250 mg",
    "medicatie.tijdstip": uurVerschoven(-1),
    "medicatie.afgevinkt": true,
    "medicatie.door": "MvD",
    "medicatie.toelichting": "",
  },
  {
    "dier.naam": "Sem",
    "dier.foto": "",
    "dier.soort": "Kat",
    "dier.verblijf": "",
    "klant.naam": "",
    "medicatie.naam": "Oogzalf",
    "medicatie.dosering": "",
    "medicatie.tijdstip": uurVerschoven(2),
    "medicatie.afgevinkt": false,
    "medicatie.door": "",
    "medicatie.toelichting": "Beide ogen",
  },
  {
    "dier.naam": "Mimi",
    "dier.foto": "",
    "dier.soort": "Kat",
    "dier.verblijf": "Kattenkamer",
    "klant.naam": "De Vries",
    "medicatie.naam": "Prednisolon",
    "medicatie.dosering": "5 mg",
    "medicatie.tijdstip": uurVerschoven(5),
    "medicatie.afgevinkt": false,
    "medicatie.door": "",
    "medicatie.toelichting": "",
  },
];

const VOEDING: Rij[] = [
  {
    "dier.naam": "Bo",
    "dier.foto": "",
    "dier.verblijf": "Hok 3",
    "voeding.voer": "Brokken",
    "voeding.hoeveelheid": "200 gram",
    "voeding.dieet": "",
    "voeding.notitie": "Rustig laten eten",
    "voeding.tijdstip": uurVerschoven(-2),
    "voeding.gevoerd": false,
    "voeding.zonderPlan": false,
    "dier.gedrag": "Vriendelijk",
  },
  {
    "dier.naam": "Babao van de Hoge Dijk",
    "dier.foto": "",
    "dier.verblijf": "Quarantaine 1",
    "voeding.voer": "Natvoer",
    "voeding.hoeveelheid": "1 blik",
    "voeding.dieet": "Hypoallergeen",
    "voeding.notitie": "",
    "voeding.tijdstip": uurVerschoven(-1),
    "voeding.gevoerd": true,
    "voeding.zonderPlan": false,
    "dier.gedrag": "",
  },
  {
    // Geen tijd in het plan, alleen een omschrijving. Dat komt echt voor en moet leesbaar blijven.
    "dier.naam": "Mimi",
    "dier.foto": "",
    "dier.verblijf": "Kattenkamer",
    "voeding.voer": "Brokjes",
    "voeding.hoeveelheid": "een handje",
    "voeding.dieet": "",
    "voeding.notitie": "",
    "voeding.tijdstip": "na de wandeling",
    "voeding.gevoerd": false,
    "voeding.zonderPlan": false,
    "dier.gedrag": "Schuw",
  },
  {
    // Een gast zonder voedingsplan staat er wél op: een dier dat hier ligt moet eten, en dat het plan
    // ontbreekt is een gat om te zien in plaats van te verbergen.
    "dier.naam": "Sem",
    "dier.foto": "",
    "dier.verblijf": "",
    "voeding.voer": "",
    "voeding.hoeveelheid": "",
    "voeding.dieet": "",
    "voeding.notitie": "",
    "voeding.tijdstip": "",
    "voeding.gevoerd": false,
    "voeding.zonderPlan": true,
    "dier.gedrag": "",
  },
];

/** De tellingen die bij deze rijen horen, zodat een badge in het voorbeeld een echt getal toont. */
export function voorbeeldAggregaten(rijen: Rij[]): Record<string, number> {
  return {
    aantal_openstaande_doses: rijen.filter((r) => r["medicatie.afgevinkt"] === false).length,
    // Een gast zonder plan telt niet mee: er is geen moment om af te vinken, dus die teller zou
    // nooit op nul komen.
    aantal_openstaande_voerbeurten: rijen.filter(
      (r) => r["voeding.gevoerd"] === false && r["voeding.zonderPlan"] !== true,
    ).length,
  };
}

export function voorbeeldrijenVoor(schermtype: string): Rij[] {
  if (schermtype === "dashboardtegel.voeding") return VOEDING;
  if (schermtype.startsWith("dashboardtegel.medicatie") || schermtype === "scherm.medicatieoverzicht") {
    return MEDICATIE;
  }
  return [];
}

/** Blijft bestaan voor wie hem rechtstreeks gebruikte. */
export const VOORBEELD_MEDICATIE = MEDICATIE;
